import axios from "axios";
import store from "../../store/index.js";
import ESCPOSFormatter from "../../utils/formartEscPosPrint.js";

const BASE_URL = "http://localhost:9977";

/**
 * QPrinterService handles all printer-related operations
 * Uses ESCPOSFormatter for receipt formatting
 */
class QPrinterService {
  constructor() {
    this.baseUrl = BASE_URL;
    this.apiInstance = axios.create({
      baseURL: this.baseUrl,
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 10000, // 10 second timeout
    });
    this.formatter = ESCPOSFormatter;
    this.store = store;
  }

  /**
   * Get list of all available system printers
   * @returns {Promise<string[]>} Array of printer names
   */
  async getAvailablePrinters() {
    try {
      const response = await this.apiInstance.get("/printers");
      if (response.data.status === "success") {
        return response.data.printers.map((printer, index) => ({
          deviceName: printer,
          innerMacAddress: "QPrinter_" + printer + "_" + index,
          isConnected: true,
          connectionError: null,
          type: "qprinter",
        }));
      }
    } catch (error) {
      console.error("Failed to get printers:", error);
      throw new Error("Failed to get available printers");
    }
  }

  /**
   * Format date for receipts
   * @private
   */
  formatDateTime(date) {
    return new Date(date).toLocaleString("en-IN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  /**
   * Print content to a specific printer
   * @private
   */
  async printContent({ printerName, content, type = "test", printConfig }) {
    try {
      if (type === "bill") {
        content = this.formatter.printBill(content, printConfig);
      } else if (type === "kot") {
        content = this.formatter.printKOT(content, printConfig);
      }

      content = content.toString();

      let payload = {
        printerName,
        content,
      };

      await this.apiInstance.post("/print", payload);
    } catch (error) {
      console.error("Print failed:", error);
      throw new Error(`Failed to print to ${printerName}: ${error.message}`);
    }
  }

  /**
   * Print KOT (Kitchen Order Ticket)
   * @param {Object} params Print parameters
   * @param {string} params.printerName KOT printer name
   * @param {Object} params.kotData KOT details to print
   * @param {string} params.type KOT type (e.g., "New KOT", "Updated KOT", "Parcel KOT")
   * @returns {Promise<void>}
   */
  async printKOT({ printerName, kotData, type = "New KOT", printConfig }) {
    try {
      const content = this.formatter.printKOT(kotData, printConfig);

      // Send to printer
      await this.printContent({ printerName, content });
    } catch (error) {
      console.error("KOT print failed:", error);
      throw new Error(
        `Failed to print KOT to ${printerName}: ${error.message}`
      );
    }
  }

  /**
   * Print bill/invoice
   * @param {Object} params Print parameters
   * @param {string} params.printerName Bill printer name
   * @param {Object} params.billData Bill details for bill
   * @param {boolean} params.isReprint Whether this is a reprint
   * @returns {Promise<void>}
   */
  async printBill({ printerName, billData, isReprint = false, printConfig }) {
    try {
      // Generate formatted content
      const content = this.formatter.printBill(billData, printConfig);

      // Send to printer
      await this.printContent({ printerName, content });
    } catch (error) {
      console.error("Bill print failed:", error);
      throw new Error(
        `Failed to print bill to ${printerName}: ${error.message}`
      );
    }
  }

  /**
   * Test width print
   * @returns {Promise<void>}
   */
  async testWidthPrint(printConfig) {
    const content = this.formatter.WidthPrint(printConfig);
    await this.printContent({
      printerName: printConfig.deviceName,
      content,
    });
  }
}

// Export singleton instance
export const qPrinterService = new QPrinterService();
